import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import * as R from "ramda";
import { ReactElement } from "react";

import { FeatureFlag } from "../../common/posthog";
import { PromptTemplate } from "../../types/prompt-template";
import DeleteConfirmationDialog from "./components/DeleteConfirmationDialog";
import LensEditor from "./components/LensEditor";
import LensTableRow from "./components/LensTableRow";
import { TABLE_COLUMN_COUNT } from "./constants";
import useLensLibrary from "./hooks/useLensLibrary";

const LensLibrary = (): ReactElement => {
  const {
    templates, loading, error, editor, deletion,
    onCreate, onEdit, onStatusChange, onDeleteRequest
  } = useLensLibrary();

  return (
    <FeatureFlag flag="aha-lens-admin" fallback={
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h5">You don&apos;t have access to this feature.</Typography>
      </Box>
    }>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1">Lens Library</Typography>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={onCreate}>
            Create New Lens
          </Button>
        </Box>
        {error && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: "error.light" }}>
            <Typography color="error">Error loading templates: {error.message}</Typography>
          </Paper>
        )}
        {loading && R.isEmpty(templates) ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
        ) : (
          <LensTable
            templates={templates} loading={loading}
            onEdit={onEdit} onToggleStatus={onStatusChange} onDelete={onDeleteRequest}
          />
        )}
        <LensEditor
          open={editor.isOpen} onClose={editor.close} template={editor.template}
          defaultTemplate={editor.defaultTemplate} onRefetch={editor.refetch}
        />
        <DeleteConfirmationDialog
          open={deletion.isConfirming} template={deletion.template}
          onConfirm={deletion.confirm} onCancel={deletion.cancel}
        />
      </Box>
    </FeatureFlag>
  );
};

interface LensTableProps {
  templates: PromptTemplate[];
  loading: boolean;
  onEdit: (template: PromptTemplate) => void;
  onToggleStatus: (template: PromptTemplate) => void;
  onDelete: (template: PromptTemplate) => void;
}

const LensTable = ({
  templates, loading, onEdit, onToggleStatus, onDelete
}: LensTableProps): ReactElement => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Status</TableCell>
          <TableCell>Title</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>Category</TableCell>
          <TableCell>Audience</TableCell>
          <TableCell>Function</TableCell>
          <TableCell>Scope</TableCell>
          <TableCell width="200px">Modified</TableCell>
          <TableCell align="right" width="100px">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {templates.map((template) => (
          <LensTableRow
            key={template.id} template={template}
            onEdit={onEdit} onToggleStatus={onToggleStatus} onDelete={onDelete}
          />
        ))}
        {R.isEmpty(templates) && !loading && (
          <TableRow>
            <TableCell colSpan={TABLE_COLUMN_COUNT} align="center">
              <Typography variant="body1" sx={{ py: 2 }}>
                No lenses found. Create your first lens to get started.
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

export default LensLibrary;
